import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { Bell, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { Popover,
	PopoverContent,
	PopoverTrigger
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDetailModal } from './alert-detail-modal';
import { ALERTS_QUERY, type AlertViewModel, type AlertCriticality } from './alerts.gql.tsx';
import { useAuth } from '@/contexts/AuthContext';

const criticalityIcons: Record<AlertCriticality, typeof Info> = {
	INFO: Info,
	WARNING: AlertTriangle,
	CRITICAL: AlertCircle
};

const criticalityColors: Record<AlertCriticality, string> = {
	INFO: 'text-blue-500',
	WARNING: 'text-yellow-500',
	CRITICAL: 'text-red-500'
};

export const AlertsDropdown: React.FC = () => {
	const { t } = useTranslation(['alerts', 'common']);
	const { user } = useAuth();
	const [selectedAlert, setSelectedAlert] = useState<AlertViewModel | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [popoverOpen, setPopoverOpen] = useState(false);

	const { data, loading, error } = useQuery(ALERTS_QUERY, {
		variables: { first: 20 },
		fetchPolicy: 'cache-and-network',
		pollInterval: 30000 // Poll every 30 seconds for new alerts
	});

	const alerts: AlertViewModel[] = data?.alerts?.nodes ?? [];

	// Calculate unread count based on userReads
	const userId = user?.userId;
	const unreadCount = alerts.filter((alert) => {
		if (!userId) return true; // If no user, consider all as unread
		return !alert.userReads?.some((read: any) => read.userId === userId);
	}).length;

	const handleAlertClick = (alert: AlertViewModel) => {
		setSelectedAlert(alert);
		setIsModalOpen(true);
		setPopoverOpen(false);
	};

	const formatTimeAgo = (dateString: string) => {
		const date = new Date(dateString);
		const now = new Date();
		const diffMs = now.getTime() - date.getTime();
		const diffMins = Math.floor(diffMs / 60000);
		const diffHours = Math.floor(diffMs / 3600000);
		const diffDays = Math.floor(diffMs / 86400000);

		if (diffMins < 1) return t('alerts:time.justNow');
		if (diffMins < 60) return t('alerts:time.minutesAgo', { count: diffMins });
		if (diffHours < 24) return t('alerts:time.hoursAgo', { count: diffHours });
		return t('alerts:time.daysAgo', { count: diffDays });
	};

	const isAlertRead = (alert: AlertViewModel) => {
		if (!userId) return false;
		return alert.userReads?.some((read: any) => read.userId === userId);
	};

	return (
		<>
			<Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
				<PopoverTrigger asChild>
					<button
						type="button"
						className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors dark:text-[#94A3B8] dark:hover:text-[#F1F5F9] dark:hover:bg-[rgba(51,65,85,0.5)]"
						title={t('alerts:title')}
					>
						<Bell className="w-5 h-5" />
						{unreadCount > 0 && (
							<span className="absolute flex items-center justify-center w-4 h-4 text-xs text-white bg-red-500 rounded-full -top-1 -right-1 lg:h-5 lg:w-5">
								{unreadCount > 9 ? '9+' : unreadCount}
							</span>
						)}
					</button>
				</PopoverTrigger>
				<PopoverContent
					className="w-80 p-0"
					align="end"
					sideOffset={8}
				>
					<div className="flex items-center justify-between px-4 py-3 border-b">
						<h3 className="font-semibold text-sm">
							{t('alerts:title')}
						</h3>
						{unreadCount > 0 && (
							<span className="text-xs text-muted-foreground">
								{t('alerts:unreadCount', { count: unreadCount })}
							</span>
						)}
					</div>

					<ScrollArea className="h-[300px]">
						{loading && !data ? (
							<div className="p-4 space-y-3">
								{[1, 2, 3].map((i) => (
									<div key={i} className="flex gap-3">
										<Skeleton className="w-8 h-8 rounded-full" />
										<div className="flex-1 space-y-2">
											<Skeleton className="h-4 w-3/4" />
											<Skeleton className="h-3 w-1/2" />
										</div>
									</div>
								))}
							</div>
						) : error ? (
							<div className="p-4 text-center text-sm text-muted-foreground">
								{t('alerts:error')}
							</div>
						) : alerts.length === 0 ? (
							<div className="p-8 text-center">
								<Bell className="w-10 h-10 mx-auto text-muted-foreground/50 mb-2" />
								<p className="text-sm text-muted-foreground">
									{t('alerts:empty')}
								</p>
							</div>
						) : (
							<div className="divide-y">
								{alerts.map((alert) => {
									const Icon = criticalityIcons[alert.criticality] || Info;
									const colorClass = criticalityColors[alert.criticality] || 'text-gray-500';
									const isRead = isAlertRead(alert);

									return (
										<button
											key={alert.alertId}
											type="button"
											onClick={() => handleAlertClick(alert)}
											className={`w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors ${
												!isRead ? 'bg-muted/30' : ''
											}`}
										>
											<div className="flex gap-3">
												<div className={`mt-0.5 ${colorClass}`}>
													<Icon className="w-5 h-5" />
												</div>
												<div className="flex-1 min-w-0">
													<div className="flex items-start justify-between gap-2">
														<p className={`text-sm font-medium truncate ${
															!isRead ? 'text-foreground' : 'text-muted-foreground'
														}`}>
															{t(`alerts:codes.${alert.code}`, alert.code)}
														</p>
														{!isRead && (
															<span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5" />
														)}
													</div>
													<p className="text-xs text-muted-foreground truncate mt-0.5">
														{alert.payload || t(`alerts:codes.${alert.code}_description`, '')}
													</p>
													<p className="text-xs text-muted-foreground/70 mt-1">
														{formatTimeAgo(alert.createdAt)}
													</p>
												</div>
											</div>
										</button>
									);
								})}
							</div>
						)}
					</ScrollArea>
				</PopoverContent>
			</Popover>

			<AlertDetailModal
				alert={selectedAlert}
				open={isModalOpen}
				onOpenChange={setIsModalOpen}
			/>
		</>
	);
};
